import { apiClient } from "api";
import { AxiosResponse } from "axios";
import {
  TAccountRolePrivileges,
  TDocumentResponse,
  TEditUser,
  TRegisterUser,
  TRole,
  TUser,
  UserStatus
} from "./models/types";

export const getAllUsersList = async (accountId: string): Promise<string[]> => {
  const response = await apiClient.get(`users/list/${accountId}`);
  return response.data;
};

export const getAllUsers = async (accountId: string): Promise<TUser[]> => {
  const response = await apiClient.get(`users/${accountId}`, {
    params: { includeInactiveUsers: true }
  });
  return response.data;
};

export const getUser = async (accountId: string, userId: string): Promise<TEditUser> => {
  const response = await apiClient.get(`users/edit/${accountId}/${userId}`);
  return response.data;
};

export const getUserLogs = async (
  accountId: string,
  userId: string
): Promise<TDocumentResponse> => {
  try {
    const response = await apiClient.get<Blob>(
      `dScribeJobs/DownloadUserLog?userId=${userId}&accountId=${accountId}`
    );
    const contentDisposition = response.headers["content-disposition"];
    let filename = "document.txt";

    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }
    }

    if (filename == `document.txt`) {
      throw new Error("file could not be fetched");
    }
    return {
      blob: response.data,
      filename,
      contentLength: Number(response.headers["content-length"] ?? 0)
    };
  } catch (err: any) {
    console.error(err);
    if ((err.response.data = "DirectoryNotFound"))
      throw new Error("No logs found for this user");
    throw new Error(`File could not be fetched ${err}`);
  }
};

export const toggleUserStatus = async (user: TUser): Promise<TUser> => {
  const response = await apiClient.put(
    `users/${user.userStatus === UserStatus.Active ? "disable" : "enable"}/${user.accountId}/${
      user.userId
    }`
  );
  return response.data;
};

export const addOrUpdateUser = async (user: TEditUser, isNew: boolean): Promise<TUser> => {
  if (isNew) {
    const response = await apiClient.post("users", user);
    return response.data;
  }
  const response = await apiClient.put("users", user);
  return response.data;
};

export const registerUser = async (registerUser: TRegisterUser): Promise<TUser> => {
  const response = await apiClient.post("users/register", registerUser);
  return response.data;
};

export const unregisterUser = async (user: TUser): Promise<TUser> => {
  const response = await apiClient.put(
    `users/unregister/${user.accountId}/${user.userId}`,
    registerUser
  );
  return response.data;
};

export const changePassword = async (
  ciamUserId: string,
  password: string
): Promise<AxiosResponse> => {
  return apiClient.put("users/changePassword", { ciamUserId, password });
};

export const getAllRolesList = async (accountId: string): Promise<TRole[]> => {
  const response = await apiClient.get(`roles/list/${accountId}`);
  return response.data;
};

export const getAllRoles = async (accountId: string): Promise<TAccountRolePrivileges> => {
  const response = await apiClient.get(`roles/${accountId}`);
  return response.data;
};

export const saveRole = async (accountId: string, role: Partial<TRole>) => {
  const payload = {
    roleId: role.roleId,
    description: role.description,
    privileges: role.privileges?.map(p => p.id) ?? []
  };

  return apiClient({
    method: role.roleId ? "put" : "post",
    url: `roles/${accountId}`,
    data: payload
  });
};
